import React from 'react';
import { t, Trans } from '@lingui/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import appMapShowcaseEnUsSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-map-showcase-en-us.webp';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import ServiceLandingServicesWrapper from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServicesWrapper';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import {
  ROVER_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { RoverProtectCondensed } from '../../../SeoLocation/components';
import QuickFact, { QuickFactHighlight } from '../../../SeoLocation/components/QuickFact';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import servicesLinksImage from '../../components/images/dog-looking-upward.png';
import ServiceBenefits from '../../components/ServiceBenefits';
import DogWalkingServiceFaqs from '../../components/ServiceFaqs/DogWalkingServiceFaqs';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import { SERVICE_SEARCH_FORM_SUBMIT_TYPES } from '../../constants';
import { quickFactStickerChihuahua, quickFactStickerPug } from '../shared-images';

import dogWalkingHeroDesktopImage from './images/dog-walking-hero-desktop.jpg';
import dogWalkingHeroTabletImage from './images/dog-walking-hero-tablet.jpg';

const dogWalkingImages: HeroImageSrcSet = {
  desktop: {
    src: dogWalkingHeroDesktopImage,
    height: 500,
  },
  tablet: {
    src: dogWalkingHeroTabletImage,
    height: 500,
  },
};

const DogWalking: React.FC<Props> = ({
  countryConfig,
  language,
  serviceLinksWithDisplayName,
  serviceLandingData,
  searchResults,
  serviceTypes,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOG_WALKING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="0x" heroImageSrcSet={dogWalkingImages} upliftedContent>
        <LandingHero.Content upliftedContent>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
            showSearchFilters={false}
            submitButtonTextVariant={SERVICE_SEARCH_FORM_SUBMIT_TYPES.DOG_WALKERS}
          />
        </LandingHero.Content>
      </LandingHero>

      {/* Includes Section within component */}
      <RoverProtectCondensed countryConfig={countryConfig} mb={SERVICE_LANDING_SECTION_MARGIN_Y} />

      {/* Includes Section & margins within component */}
      <ServiceBenefits title={<Trans>Dog walks that match your pup’s energy</Trans>}>
        <ServiceBenefits.List>
          <ServiceBenefits.ListItem title={<Trans>Dog walkers you can trust</Trans>}>
            <Trans>
              We make it easy to find 5-star, background-checked sitters. Check out sitter profiles
              and read verified reviews to find a sitter your dog will look forward to staying with.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem
            title={<Trans>Easy scheduling for recurring or one-time walks</Trans>}
          >
            <Trans>
              Choose the day and time your dog needs a walk. Select a single day or repeat the walk
              each day or week.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Get photos & updates</Trans>}>
            <Trans>
              Find out how your dog is doing by requesting updates in the Rover app. Get photos from
              your sitter of everything from playtime to nap time.
            </Trans>
          </ServiceBenefits.ListItem>
        </ServiceBenefits.List>
      </ServiceBenefits>

      {searchResults && searchResults.results?.length > 0 && (
        <ServiceLandingSection>
          <LandingSearchResultsCarousel
            searchResponse={searchResults}
            searchFilters={initialSearchFilters}
            language={language}
            heading={t`Highly-rated dog walkers in your area`}
            headingVariant="display"
            serviceTypeSlug={serviceTypeSlug}
            countryConfig={countryConfig}
          />
        </ServiceLandingSection>
      )}

      {/* Includes Section within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="yellow"
        stickerImageSrc={quickFactStickerChihuahua}
      >
        <Trans>
          Over <QuickFactHighlight>1.1 million</QuickFactHighlight> dogs enjoyed local sniffing
          adventures with dog walkers on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>

      <ServiceLandingSection>
        <DogWalkingServiceFaqs
          localizedLinkUrls={localizedLinkUrls}
          language={language}
          searchFilters={initialSearchFilters}
        />
      </ServiceLandingSection>

      {/* Includes Section & margins within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="pink"
        stickerImageSrc={quickFactStickerPug}
      >
        <Trans>
          Dogs who exercise <QuickFactHighlight>at least 2x a day</QuickFactHighlight> are more
          likely to get a brain boost
        </Trans>
        <Superscript>2</Superscript>
      </QuickFact>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>How to book the perfect dog walker</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Search to find dog walkers in your neighborhood.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Meet in person</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                Connect with a dog walker who's a good fit for you, your dog, and your lifestyle.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Book &amp; pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                It’s that easy! All payments are processed through Rover's secured platform.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      {/* Includes Section & margins within component */}
      <ServiceLandingServicesWrapper
        serviceLandingType={serviceLandingType}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
      </ServiceLandingServicesWrapper>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout
          altHeading={<Trans>See how much fun your dog is having</Trans>}
          altImageSrc={appMapShowcaseEnUsSource}
        />
      </ServiceLandingSection>

      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
        citations={[
          ROVER_DATA_CITATION(),
          <>
            Brown, S. (2024). <cite>Checklist: How to keep your dog healthy.</cite> Rover.
          </>,
        ]}
      />
    </>
  );
};

export default DogWalking;
